/**
 * LeagueSettingsPage — /league/:leagueId/settings
 * ─────────────────────────────────────────────────────────────────
 * Edit league-level metadata: name, description, logo URL,
 * public-page toggle, and the adult-name-display flag.
 *
 * Permission gate: only members with manage_league_settings (or
 * managers, who have all permissions implicitly). The page itself
 * checks this and renders a polite "no access" if the user
 * navigates here without permission; the backend re-checks via
 * requireLeaguePermission('manage_league_settings') on every PATCH.
 *
 * Intentionally NOT editable in Phase 2a:
 *   • slug — once a league has a slug, links to it (chat mentions,
 *     bookmarks, future public pages) depend on it. A rename flow
 *     would need redirects and is deferred.
 *   • sport — the schema's CHECK constraint only permits 'hockey'
 *     in Phase 1. When more sports land, this becomes a one-time
 *     pick rather than freely editable.
 *   • public_show_full_names_minors — PRIVACY FLOOR. The schema
 *     stores the column but the public surface ignores it. No UI.
 *   • archive — manager-only, exposed via a separate Danger Zone
 *     section at the bottom.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, AlertTriangle, Archive, Upload, Trophy, Plus, Trash2, Globe, ExternalLink, Check, Copy } from 'lucide-react';
// v2.5.132 — markdown editor for the About content block.
import MarkdownEditor from '../../components/MarkdownEditor.jsx';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership, invalidateLeagueMembershipCache } from '../../hooks/useLeagueMembership.js';

export default function LeagueSettingsPage() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);

  const [league,  setLeague]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  // v2.5.131 — separate upload-busy state from the main save
  // state so the "Replace logo" button can show "Uploading…"
  // independently of the "Save changes" button.
  const [logoUploading, setLogoUploading] = useState(false);

  // Form state — mirrors editable fields. Initialized from the
  // server response so unchanged fields stay unchanged on PATCH.
  // v2.5.131 — logo_url removed from form state: it's no longer
  // edited via the PATCH body; the upload endpoint sets it
  // server-side. The current value is read from `league.logo_url`
  // directly when rendering the preview.
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    is_public: false,
    public_show_full_names_adults: false,
  });

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const l = data?.league;
        setLeague(l);
        if (l) {
          setForm({
            name: l.name || '',
            slug: l.slug || '',
            description: l.description || '',
            is_public: !!l.is_public,
            public_show_full_names_adults: !!l.public_show_full_names_adults,
          });
        }
      })
      .catch((err) => {
        if (err?.response?.status === 403) toast.error('You are not a member of this league');
        else if (err?.response?.status !== 401) toast.error('Could not load league');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId]);

  const canEdit = isManager || can('manage_league_settings');

  async function handleSave() {
    if (!canEdit) {
      toast.error('You do not have permission to edit settings');
      return;
    }
    if (form.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    // v2.5.131 — slug rename. If the slug has changed, warn the
    // manager that existing /l/<old-slug>/ links will break. This
    // is unavoidable — we don't currently keep a slug-history
    // table — so the safest thing is to make the consequence
    // explicit and have the manager confirm. Empty change-set =
    // no warning.
    const slugChanged = form.slug.trim().toLowerCase() !== (league?.slug || '');
    if (slugChanged) {
      const ok = window.confirm(
        `Rename the slug from "${league?.slug}" to "${form.slug.trim().toLowerCase()}"?\n\n` +
        'Any existing /l/' + (league?.slug || '') + '/ links you\'ve shared will stop working ' +
        'and will need to be re-shared with the new URL.'
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      // v2.5.131 — logo_url is no longer sent in the PATCH body.
      // The dedicated POST /api/leagues/:id/logo endpoint sets it
      // server-side after writing the uploaded file to disk, so a
      // client-side PATCH could otherwise overwrite the
      // canonical URL with stale form state.
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        is_public: form.is_public,
        public_show_full_names_adults: form.public_show_full_names_adults,
      };
      if (slugChanged) payload.slug = form.slug.trim().toLowerCase();
      const { data } = await api.patch(`/leagues/${leagueId}`, payload);
      setLeague(data?.league || league);
      invalidateLeagueMembershipCache(leagueId);  // settings affect cached league row
      toast.success('Settings saved');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save settings';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  // v2.5.131 — logo upload handler.
  // The <input type="file"> fires this on change. We read the
  // chosen file as base64 (FileReader.readAsDataURL), strip the
  // data-URL prefix, then POST to /api/leagues/:id/logo. The
  // backend validates MIME + magic bytes + size cap (2 MB raw),
  // writes to disk under LEAGUE_LOGO_DIR/<id>/logo.<ext>, and
  // returns the new logo_url with a cache-busting timestamp.
  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canEdit) {
      toast.error('You do not have permission to change the logo');
      return;
    }
    // Client-side gate so we don't waste a round-trip on
    // obviously-wrong files. Backend re-checks all of these.
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Logo must be PNG, JPEG, or WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo too large — 2 MB cap');
      return;
    }
    setLogoUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // FileReader yields a data: URL; strip the prefix so the
          // backend sees clean base64.
          const result = String(reader.result || '');
          const comma = result.indexOf(',');
          resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post(
        `/leagues/${leagueId}/logo`,
        { data: base64, mimeType: file.type }
      );
      // Update league row so the preview reflects the new logo
      // immediately. The ?v=<ts> query string the server appends
      // is enough to bypass the 5-min image cache.
      setLeague((prev) => prev ? { ...prev, logo_url: data.logo_url } : prev);
      toast.success('Logo updated');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not upload logo';
      toast.error(msg);
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleLogoRemove() {
    if (!canEdit) return;
    if (!window.confirm('Remove the league logo?')) return;
    setLogoUploading(true);
    try {
      await api.delete(`/leagues/${leagueId}/logo`);
      setLeague((prev) => prev ? { ...prev, logo_url: null } : prev);
      toast.success('Logo removed');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not remove logo';
      toast.error(msg);
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleArchive() {
    if (!isManager) {
      toast.error('Only managers can archive a league');
      return;
    }
    // Two-step confirm — archiving hides the league from active
    // lists. Reversal currently requires a DB tweak (no unarchive
    // route in Phase 1).
    const ok = window.confirm(
      'Archive this league? It will be hidden from active lists for all members. ' +
      'This cannot be undone from the UI in this version.'
    );
    if (!ok) return;
    try {
      await api.post(`/leagues/${leagueId}/archive`);
      toast.success('League archived');
      invalidateLeagueMembershipCache(leagueId);
      navigate('/leagues');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to archive';
      toast.error(msg);
    }
  }

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 720 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          Loading…
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 720 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or you don't have access.
        </div>
      </div>
    );
  }

  // Permission gate. The user can navigate here directly (the sub-nav
  // hides the entry for non-permissioned admins, but a bookmark works).
  // We render a polite explanation rather than a 403.
  if (!canEdit) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 720 }}>
        <LeagueSubNav league={league} />
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <AlertTriangle size={36} color="var(--puck)" style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No access</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Editing league settings requires the <code>manage_league_settings</code> permission.
            Ask a manager to grant it from the Members tab.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 720 }}>
      <LeagueSubNav league={league} />

      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="section-title text-lg" style={{ marginBottom: 16 }}>League info</h2>

        <Field label="Name" hint="Shown everywhere this league appears.">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            maxLength={150}
            style={inputStyle}
          />
        </Field>

        <Field label="Description" hint="Short blurb shown on the dashboard. Optional.">
          <textarea
            value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            maxLength={500}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>

        {/* v2.5.131 — logo upload.
            Replaces the previous free-text URL input. The file is
            POSTed as base64 to /api/leagues/:id/logo, the backend
            validates + stores on disk, and updates leagues.logo_url
            to a stable /api/public/leagues/<id>/logo?v=<ts> URL.
            We refresh the league row after upload so the preview
            shows the new image immediately. */}
        <Field label="Logo" hint="PNG, JPEG, or WebP. Up to 2 MB. Shown in the dashboard header and (when enabled) on the public page.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {/* Preview */}
            <div style={{
              width: 64, height: 64, borderRadius: 12,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', flexShrink: 0,
            }}>
              {league.logo_url ? (
                <img src={league.logo_url} alt="" style={{
                  width: '100%', height: '100%', objectFit: 'contain',
                }} />
              ) : (
                <Trophy size={26} color="var(--text-3)" />
              )}
            </div>
            {/* Upload + remove controls. Stack on narrow screens via
                flex-wrap on the parent. */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label
                className={canEdit ? 'btn-ice' : 'btn-ghost'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  cursor: canEdit && !logoUploading ? 'pointer' : 'not-allowed',
                  opacity: logoUploading ? 0.6 : 1,
                  fontSize: 13, padding: '8px 14px',
                }}>
                <Upload size={13} />
                {logoUploading
                  ? 'Uploading…'
                  : (league.logo_url ? 'Replace logo' : 'Upload logo')}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={!canEdit || logoUploading}
                  onChange={handleLogoUpload}
                  // Reset value after each pick so re-picking the
                  // same file fires onChange again (otherwise
                  // selecting the same file is a no-op).
                  onClick={(e) => { e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
              </label>
              {league.logo_url && canEdit && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleLogoRemove}
                  disabled={logoUploading}
                  style={{ fontSize: 13, padding: '8px 14px' }}>
                  Remove
                </button>
              )}
            </div>
          </div>
        </Field>

        <Field label="URL slug" hint="2-40 chars, lowercase letters, numbers, and hyphens. Renaming will break any links to /l/<old-slug>/ that you've already shared.">
          <input
            type="text"
            value={form.slug}
            // v2.5.131 — slug is now editable. Normalize keystrokes
            // to lowercase + a-z0-9-hyphen on the fly so the user
            // sees exactly the slug that will be saved. Reject
            // anything else silently rather than 400'ing on save.
            onChange={(e) => {
              const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
              setForm(f => ({ ...f, slug: v }));
            }}
            disabled={!canEdit}
            maxLength={40}
            style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
            Public page will be at /l/{form.slug || '<slug>'}/
          </div>
        </Field>

        <Field label="Sport" hint="Hockey only for now. Multi-sport support coming with stats expansion.">
          <input type="text" value={league.sport} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed', textTransform: 'capitalize' }} />
        </Field>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="section-title text-lg" style={{ marginBottom: 4 }}>Public page</h2>
        <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 16 }}>
          Controls for your public league website at /l/{league.slug} — news, schedule, standings,
          team pages, stats, playoffs and the registration page. Articles, coach profiles and sponsors
          are edited under Public site.
        </p>

        <ToggleRow
          label="Enable public page"
          hint="When enabled, anyone with the URL below can see the league's schedule, standings, and rosters (filtered for privacy)."
          checked={form.is_public}
          onChange={(v) => setForm(f => ({ ...f, is_public: v }))}
        />

        {/* v2.5.131 — public URL display.
            Uses the SAVED state (league.is_public, league.slug) —
            NOT the live form state. Reason: the Copy and Open
            buttons would otherwise produce URLs that haven't been
            saved yet, leading the manager to share a link that
            doesn't work. If the manager has unsaved changes that
            would change the URL, we show a hint nudging them to
            save first. */}
        {league.is_public && league.slug && (
          <div style={{
            marginTop: 8, marginBottom: 14, padding: 12,
            background: 'var(--surface-2)', borderRadius: 10,
            border: '1px solid var(--ice-a25)',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: 'var(--text-2)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                marginBottom: 4,
              }}>
                Public URL
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text)',
                wordBreak: 'break-all',
              }}>
                {window.location.origin}/l/{league.slug}/
              </div>
              {/* Unsaved-change hint: if the form's slug or
                  is_public differs from saved state, the URL above
                  is stale relative to what the user has typed. */}
              {(form.slug.trim().toLowerCase() !== league.slug ||
                form.is_public !== league.is_public) && (
                <div style={{ fontSize: 11, color: 'var(--puck)', marginTop: 6 }}>
                  Save your pending changes to update this URL.
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-ghost"
              style={{ fontSize: 12, padding: '6px 12px' }}
              onClick={() => {
                const url = `${window.location.origin}/l/${league.slug}/`;
                navigator.clipboard?.writeText(url)
                  .then(() => toast.success('URL copied'))
                  .catch(() => toast.error('Could not copy — select and copy manually'));
              }}>
              Copy URL
            </button>
            <a
              href={`/l/${league.slug}/`}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost"
              style={{ fontSize: 12, padding: '6px 12px', textDecoration: 'none' }}>
              Open ↗
            </a>
          </div>
        )}

        <ToggleRow
          label="Show full names for adult players"
          hint="When OFF, all players appear as initials on the public page. Individual adults can also opt out via their profile."
          checked={form.public_show_full_names_adults}
          onChange={(v) => setForm(f => ({ ...f, public_show_full_names_adults: v }))}
        />
        <div style={{
          marginTop: 12, padding: 12,
          background: 'var(--surface-2)', borderRadius: 10,
          fontSize: 12, color: 'var(--text-2)',
        }}>
          <strong style={{ color: 'var(--text)' }}>Privacy floor:</strong> minor players are always
          shown as initials on the public page. This cannot be overridden.
        </div>
      </div>

      {/* v2.5.132 — Public-page CONTENT card.
          Manager-editable blocks that populate the designed
          landing page. Hidden unless is_public is enabled, since
          the content has no surface until the public page is on. */}
      {form.is_public && (
        <PublicPageContentCard
          leagueId={leagueId}
          canEdit={canEdit}
        />
      )}

      {/* v2.5.132 — Custom domains card. Shown to all managers
          regardless of is_public toggle so they can register
          domains in advance. Verification + cert provisioning
          arrives in a future TLS-work batch; for now this is
          a registration + management surface. */}
      <CustomDomainsCard
        leagueId={leagueId}
        canEdit={canEdit}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <button
          type="button"
          className="btn-ice"
          onClick={handleSave}
          disabled={saving}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* Danger Zone — managers only. */}
      {isManager && (
        <div className="card" style={{ borderColor: 'var(--penalty-a30)' }}>
          <h2 className="section-title text-lg" style={{ marginBottom: 12, color: 'var(--penalty)' }}>
            Danger zone
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
            Archiving hides this league from active lists. Members can no longer see it in their
            league list. Past games and stats are preserved.
          </p>
          <button
            type="button"
            className="btn-ghost"
            onClick={handleArchive}
            style={{
              borderColor: 'var(--penalty-a30)',
              color: 'var(--penalty)',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Archive size={14} />
            Archive league
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 16 }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</span>}
    </label>
  );
}

function ToggleRow({ label, hint, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        style={{
          width: 44, height: 24, borderRadius: 12, flexShrink: 0,
          background: checked ? 'var(--ice)' : 'var(--surface-2)',
          border: '1px solid var(--border)',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background 0.18s',
          padding: 0,
        }}
      >
        <span style={{
          position: 'absolute', top: 2, left: checked ? 22 : 2,
          width: 18, height: 18, borderRadius: '50%',
          background: checked ? 'var(--btn-accent-text)' : 'var(--text)',
          transition: 'left 0.18s',
        }} />
      </button>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 14px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  outline: 'none',
};

// v2.5.132 — curated theme palette. 10 colors chosen for legibility
// on the public page (sufficient contrast against the ice/midnight
// background variants, distinguishable from each other, broadly
// suitable for hockey orgs which lean toward bold-but-not-loud
// brand colors). The manager picks one; the public page uses it
// for buttons, links, and hero accents.
//
// Hex values stored in the theme_color content block. Free hex
// entry is deliberately not exposed in v1 — this curated set
// protects design quality while still giving real choice.
const THEME_PALETTE = [
  { name: 'Ice blue',     value: '#0ea5e9' },
  { name: 'Royal blue',   value: '#1d4ed8' },
  { name: 'Midnight',     value: '#1e293b' },
  { name: 'Emerald',      value: '#059669' },
  { name: 'Forest',       value: '#15803d' },
  { name: 'Crimson',      value: '#dc2626' },
  { name: 'Burgundy',     value: '#9f1239' },
  { name: 'Amber',        value: '#d97706' },
  { name: 'Plum',         value: '#7c3aed' },
  { name: 'Slate',        value: '#475569' },
];

// ─────────────────────────────────────────────────────────────────
// PublicPageContentCard
// ─────────────────────────────────────────────────────────────────
//
// Manager-editable content for the league's public landing page.
// Reads/writes /api/leagues/:id/content (PUT body is { blocks: {...} }).
// Blocks are loaded on mount; the user edits locally; Save flushes
// to the backend. Save is at the card level rather than the page
// level because the public-page content is conceptually separate
// from league settings (different audience, different cadence).
function PublicPageContentCard({ leagueId, canEdit }) {
  const [blocks, setBlocks] = useState(null);
  const [draft,  setDraft]  = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}/content`)
      .then(({ data }) => {
        if (cancelled) return;
        setBlocks(data?.blocks || {});
        setDraft(data?.blocks || {});
      })
      .catch(() => { /* leave null; user sees loading state */ });
    return () => { cancelled = true; };
  }, [leagueId]);

  // Track whether the draft differs from the saved blocks so the
  // Save button can disable when there's nothing to save.
  const dirty = blocks !== null && JSON.stringify(blocks) !== JSON.stringify(draft);

  function setBlock(key, value) {
    setDraft(d => ({ ...d, [key]: value }));
  }

  async function handleSave() {
    if (!canEdit || !dirty) return;
    setSaving(true);
    try {
      // Only send blocks that changed. Reduces payload + makes
      // partial-failure semantics clear server-side.
      const payload = {};
      for (const k of Object.keys(draft)) {
        if (draft[k] !== (blocks?.[k] ?? null)) {
          payload[k] = draft[k] === '' ? null : draft[k];
        }
      }
      // Also pick up keys that were deleted entirely from draft
      // (if the UI ever supports clearing — current UI uses empty
      // string for clear, normalized above).
      for (const k of Object.keys(blocks || {})) {
        if (!(k in draft)) payload[k] = null;
      }
      const { data } = await api.put(`/leagues/${leagueId}/content`, { blocks: payload });
      setBlocks(data?.blocks || {});
      setDraft(data?.blocks || {});
      toast.success('Public page updated');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save public page content';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (blocks === null) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 className="section-title text-lg" style={{ marginBottom: 12 }}>Public page content</h2>
        <p style={{ color: 'var(--text-2)', fontSize: 13 }}>Loading…</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <h2 className="section-title text-lg" style={{ marginBottom: 8 }}>Public page content</h2>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
        Manager-editable content for the public landing page at /l/{leagueId && '<slug>'}. Leave any
        field blank to use the default.
      </p>

      {/* Hero */}
      <Field label="Hero tagline" hint="Short line under the league name on the public homepage (e.g. “Kitchener's home for youth hockey since 1959”). Max 200 chars.">
        <input
          type="text"
          value={draft.hero_tagline || ''}
          onChange={(e) => setBlock('hero_tagline', e.target.value)}
          disabled={!canEdit}
          maxLength={200}
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      <Field label="Call-to-action button text" hint="The primary action button in the hero. Leave blank for no button.">
        <input
          type="text"
          value={draft.hero_cta_text || ''}
          onChange={(e) => setBlock('hero_cta_text', e.target.value)}
          disabled={!canEdit}
          maxLength={40}
          placeholder="e.g. Register for 2026/27"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      <Field label="Call-to-action button link" hint="Where the button goes. Use a full URL or a path starting with /.">
        <input
          type="text"
          value={draft.hero_cta_link || ''}
          onChange={(e) => setBlock('hero_cta_link', e.target.value)}
          disabled={!canEdit}
          maxLength={500}
          placeholder="https://example.com/register"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      {/* About — markdown editor */}
      <Field label="About" hint="Long-form description shown on the public homepage. Markdown supported — use the toolbar or type formatting directly.">
        <MarkdownEditor
          value={draft.about_markdown || ''}
          onChange={(v) => setBlock('about_markdown', v)}
          placeholder="Tell visitors about your league…"
          disabled={!canEdit}
          rows={8}
        />
      </Field>

      {/* Theme palette */}
      <Field label="Theme color" hint="Used for buttons, links, and the hero accent on the public page.">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {THEME_PALETTE.map((c) => {
            const selected = (draft.theme_color || '').toLowerCase() === c.value.toLowerCase();
            return (
              <button
                key={c.value}
                type="button"
                disabled={!canEdit}
                onClick={() => setBlock('theme_color', c.value)}
                title={c.name}
                aria-label={c.name}
                aria-pressed={selected}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: c.value,
                  border: selected ? '2px solid var(--text)' : '1px solid var(--border)',
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                  boxShadow: selected ? 'var(--shadow-md)' : 'none',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {/* Check icon on the selected swatch. Color logic: pure-white
                    text would be unreadable on light swatches; pure-black
                    same on dark swatches. Crude luminance gate. */}
                {selected && <Check size={14} color={isLightColor(c.value) ? '#000' : '#fff'} />}
              </button>
            );
          })}
          {/* Clear (no theme) option — sits at the end of the row. */}
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => setBlock('theme_color', '')}
            title="No theme color (use default)"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'var(--surface-2)',
              border: !draft.theme_color ? '2px solid var(--text)' : '1px solid var(--border)',
              cursor: canEdit ? 'pointer' : 'not-allowed',
              color: 'var(--text-2)',
              fontSize: 11,
            }}>
            None
          </button>
        </div>
      </Field>

      {/* Footer */}
      <h3 style={{ fontSize: 13, fontWeight: 700, marginTop: 24, marginBottom: 8, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Footer
      </h3>

      <Field label="Address" hint="Optional. Shown in the footer for visitors who want to mail you.">
        <textarea
          value={draft.footer_address || ''}
          onChange={(e) => setBlock('footer_address', e.target.value)}
          disabled={!canEdit}
          maxLength={500}
          rows={2}
          placeholder="123 Main St, Kitchener ON, N2G 1A1"
          style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
        />
      </Field>

      <Field label="Email">
        <input
          type="email"
          value={draft.footer_email || ''}
          onChange={(e) => setBlock('footer_email', e.target.value)}
          disabled={!canEdit}
          maxLength={254}
          placeholder="contact@example.com"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      <Field label="Phone">
        <input
          type="tel"
          value={draft.footer_phone || ''}
          onChange={(e) => setBlock('footer_phone', e.target.value)}
          disabled={!canEdit}
          maxLength={40}
          placeholder="+1 519 555 0100"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      <Field label="Facebook URL">
        <input
          type="url"
          value={draft.social_facebook || ''}
          onChange={(e) => setBlock('social_facebook', e.target.value)}
          disabled={!canEdit}
          maxLength={500}
          placeholder="https://facebook.com/your-league"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      <Field label="X (Twitter) URL">
        <input
          type="url"
          value={draft.social_x || ''}
          onChange={(e) => setBlock('social_x', e.target.value)}
          disabled={!canEdit}
          maxLength={500}
          placeholder="https://x.com/your-league"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      <Field label="Instagram URL">
        <input
          type="url"
          value={draft.social_instagram || ''}
          onChange={(e) => setBlock('social_instagram', e.target.value)}
          disabled={!canEdit}
          maxLength={500}
          placeholder="https://instagram.com/your-league"
          style={canEdit ? inputStyle : { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }}
        />
      </Field>

      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            className="btn-ice"
            onClick={handleSave}
            disabled={saving || !dirty}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Save size={14} />
            {saving ? 'Saving…' : (dirty ? 'Save public page' : 'No changes')}
          </button>
        </div>
      )}
    </div>
  );
}

// Trivial luminance check so the check icon inside a swatch shows
// in a readable color. Uses the standard W3C relative-luminance
// formula simplified: light colors (lum > 0.6) get dark text.
function isLightColor(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  // Naive (not gamma-corrected) but good enough for picking ink color.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 0.6;
}

// ─────────────────────────────────────────────────────────────────
// CustomDomainsCard
// ─────────────────────────────────────────────────────────────────
//
// Manager UI for registering, verifying, and going-live with
// custom domains. TLS provisioning is deferred (see the verify
// endpoint comment in routes/leagues.js); this UI is correct
// for the workflow once that work lands.
function CustomDomainsCard({ leagueId, canEdit }) {
  const [domains, setDomains] = useState(null);
  const [adding, setAdding]   = useState(false);
  const [newHost, setNewHost] = useState('');

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}/custom-domains`)
      .then(({ data }) => { if (!cancelled) setDomains(data?.domains || []); })
      .catch(() => { if (!cancelled) setDomains([]); });
    return () => { cancelled = true; };
  }, [leagueId]);

  async function handleAdd() {
    if (!canEdit) return;
    const host = newHost.trim().toLowerCase();
    if (!host) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/custom-domains`, { hostname: host });
      setDomains((prev) => [...(prev || []), data.domain]);
      setNewHost('');
      toast.success('Domain registered — follow the DNS setup below');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to register domain');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(domainId, hostname) {
    if (!canEdit) return;
    if (!window.confirm(`Remove ${hostname}? Any traffic to this hostname will stop resolving to your league page.`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/custom-domains/${domainId}`);
      setDomains((prev) => prev.filter(d => d.id !== domainId));
      toast.success('Domain removed');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove domain');
    }
  }

  async function handleToggleLive(domain) {
    if (!canEdit) return;
    try {
      const { data } = await api.patch(`/leagues/${leagueId}/custom-domains/${domain.id}`, {
        is_live: !domain.is_live,
      });
      setDomains((prev) => prev.map(d => d.id === domain.id ? data.domain : d));
      toast.success(data.domain.is_live ? 'Domain is now live' : 'Domain disabled');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not change status');
    }
  }

  function copyToken(token) {
    navigator.clipboard?.writeText(token)
      .then(() => toast.success('Token copied'))
      .catch(() => toast.error('Could not copy'));
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Globe size={18} color="var(--ice)" />
        <h2 className="section-title text-lg" style={{ margin: 0 }}>Custom domains</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
        Point your own domain (e.g. <code style={{ fontFamily: 'var(--font-mono)' }}>www.kmha.ca</code>) at your league public page.
        Verification and automatic TLS arrive in a future release; for now you can register and the operator can flip you live manually.
      </p>

      {/* Add form */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newHost}
            onChange={(e) => setNewHost(e.target.value)}
            placeholder="www.kmha.ca"
            disabled={adding}
            style={{ ...inputStyle, flex: 1, minWidth: 200 }}
          />
          <button
            type="button"
            className="btn-ice"
            onClick={handleAdd}
            disabled={adding || !newHost.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} />
            {adding ? 'Adding…' : 'Add domain'}
          </button>
        </div>
      )}

      {/* List */}
      {domains === null ? (
        <p style={{ color: 'var(--text-2)', fontSize: 13 }}>Loading…</p>
      ) : domains.length === 0 ? (
        <p style={{ color: 'var(--text-2)', fontSize: 13 }}>No custom domains registered yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {domains.map((d) => (
            <DomainRow
              key={d.id}
              domain={d}
              canEdit={canEdit}
              onDelete={() => handleDelete(d.id, d.hostname)}
              onToggleLive={() => handleToggleLive(d)}
              onCopyToken={() => copyToken(d.verification_token)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DomainRow({ domain, canEdit, onDelete, onToggleLive, onCopyToken }) {
  // Step indicator: registered → verified → cert provisioned → live
  const step = domain.is_live ? 'live'
    : (domain.cert_provisioned_at ? 'cert_ready'
       : (domain.verified_at ? 'verified' : 'pending'));
  const statusLabel = {
    pending:    'Pending DNS verification',
    verified:   'Verified · awaiting cert',
    cert_ready: 'Ready to go live',
    live:       'Live',
  }[step];
  const statusColor = {
    pending:    'var(--text-2)',
    verified:   'var(--ice)',
    cert_ready: 'var(--ice)',
    live:       'var(--banner)',
  }[step];

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: 14,
      background: 'var(--surface)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700 }}>
            {domain.hostname}
          </div>
          <div style={{ fontSize: 12, color: statusColor, marginTop: 4, fontWeight: 600 }}>
            {statusLabel}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {step === 'cert_ready' && canEdit && (
            <button type="button" className="btn-ice" onClick={onToggleLive}
                    style={{ fontSize: 12, padding: '6px 12px' }}>
              Go live
            </button>
          )}
          {step === 'live' && canEdit && (
            <button type="button" className="btn-ghost" onClick={onToggleLive}
                    style={{ fontSize: 12, padding: '6px 12px' }}>
              Disable
            </button>
          )}
          {step === 'live' && (
            <a
              href={`https://${domain.hostname}/`}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost"
              style={{ fontSize: 12, padding: '6px 10px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={12} />
              Open
            </a>
          )}
          {canEdit && (
            <button type="button" className="btn-ghost" onClick={onDelete}
                    style={{ fontSize: 12, padding: '6px 10px', color: 'var(--penalty)' }}
                    title="Remove">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {/* DNS setup instructions, shown until verified */}
      {!domain.verified_at && (
        <div style={{
          marginTop: 14, padding: 12,
          background: 'var(--surface-2)', borderRadius: 8,
          fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>
            DNS setup (steps for your domain registrar):
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', lineHeight: 1.6 }}>
            <li>
              Add a TXT record at{' '}
              {/* wordBreak so a long custom hostname (e.g. a subdomain-heavy
                  registrar name) wraps instead of forcing the whole card —
                  and the page — into horizontal scroll at ~390px. */}
              <code style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                _gameday-verify.{domain.hostname}
              </code>{' '}
              with the verification token below.
            </li>
            <li>
              Add a CNAME record pointing <code style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{domain.hostname}</code> to <code style={{ fontFamily: 'var(--font-mono)' }}>app.game-day.app</code>.
            </li>
            <li>
              Once both records are in place, contact support so the operator can complete verification and provision a TLS certificate. (Automatic verification arrives in a future release.)
            </li>
          </ol>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Verification token
            </span>
            <code style={{
              fontFamily: 'var(--font-mono)', fontSize: 11,
              background: 'var(--surface)', padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--border)',
              wordBreak: 'break-all', flex: 1, minWidth: 200,
            }}>
              {domain.verification_token}
            </code>
            <button type="button" className="btn-ghost" onClick={onCopyToken}
                    style={{ fontSize: 11, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Copy size={11} />
              Copy
            </button>
          </div>
        </div>
      )}

      {domain.last_check_error && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--penalty)' }}>
          Last check: {domain.last_check_error}
        </div>
      )}
    </div>
  );
}
